import { useQueryClient } from "@tanstack/react-query";
import { print } from "graphql";

import { graphql } from "lib/generated/app-service-gql";
import {
  DocumentBuyingSignalsUpdatesSubscription,
  DocumentBuyingSignalsUpdatesSubscriptionVariables,
  DocumentsSearchRequest,
} from "lib/generated/app-service-gql/graphql";
import { useSubscription } from "../useGraphQlClient";
import { documentBuyingSignalsQueryKey } from "./useDocumentBuyingSignals";

const documentBuyingSignalsUpdatesSubscriptionDoc = graphql(`
  subscription documentBuyingSignalsUpdates($operationId: String!) {
    documentBuyingSignalsUpdates(operationId: $operationId) {
      operationId
      status
      failureReason
    }
  }
`);

// `print` re-serializes the codegen-scanned AST into the string `useSubscription` requires.
const documentBuyingSignalsUpdatesSubscriptionString = print(
  documentBuyingSignalsUpdatesSubscriptionDoc,
);

type StatusEvent = DocumentBuyingSignalsUpdatesSubscription["documentBuyingSignalsUpdates"];

type Props = {
  request: DocumentsSearchRequest;
  operationId: string | null;
  enabled: boolean;
  onStatusChange?: (event: StatusEvent) => void;
};

export function useDocumentBuyingSignalsUpdates({
  request,
  operationId,
  enabled,
  onStatusChange,
}: Props) {
  const queryClient = useQueryClient();

  return useSubscription<
    DocumentBuyingSignalsUpdatesSubscription,
    DocumentBuyingSignalsUpdatesSubscriptionVariables
  >(
    documentBuyingSignalsUpdatesSubscriptionString,
    { operationId: operationId ?? "" },
    {
      enabled: enabled && !!operationId,
      onData: (data) => {
        const event = data.documentBuyingSignalsUpdates;
        onStatusChange?.(event);

        if (event.status === "completed" || event.status === "failed") {
          void queryClient.invalidateQueries({
            queryKey: documentBuyingSignalsQueryKey(request),
          });
        }
      },
    },
  );
}
